/**
 * Security utilities for validating origins and sanitizing inputs
 */

import { envConfig } from '@/config';

/**
 * Get the list of allowed origins from config
 * @returns Array of allowed origin URLs
 */
export function getAllowedOrigins(): string[] {
	const originsArr = typeof envConfig.ALLOWED_ORIGINS === 'string'
		? envConfig.ALLOWED_ORIGINS.split(',').map((o) => o.trim()).filter(Boolean)
		: [];
	// Always include the current origin in development
	if (process.env.NODE_ENV === "development") {
		const currentOrigin = window.location.origin;
		if (!originsArr.includes(currentOrigin)) {
			originsArr.push(currentOrigin);
		}
	}
	return originsArr;
}

/**
 * Validates if a message origin is allowed
 * @param origin - The origin to validate
 * @returns boolean - true if origin is allowed
 */
export function isOriginAllowed(origin: string): boolean {
	const allowedOrigins = getAllowedOrigins()
	
	// If no origins configured, log warning and reject
	if (allowedOrigins.length === 0) {
		// eslint-disable-next-line no-console
		console.warn("No allowed origins configured. PostMessage rejected.")
		return false
	}
	
	// Check if origin exactly matches any allowed origin
	const isAllowed = allowedOrigins.includes(origin)
	
	if (!isAllowed) {
		// eslint-disable-next-line no-console
		console.warn(`PostMessage from unauthorized origin rejected: ${origin}`)
	}
	
	return isAllowed
}

/**
 * Validates a postMessage event
 * @param event - The MessageEvent to validate
 * @returns boolean - true if the event is valid and from an allowed origin
 */
export function validatePostMessageEvent(event: MessageEvent): boolean {
	// Validate origin
	if (!isOriginAllowed(event.origin)) {
		return false
	}
	
	// Validate that data exists and is an object
	if (!event.data || typeof event.data !== "object") {
		// eslint-disable-next-line no-console
		console.warn("PostMessage rejected: Invalid data format")
		return false
	}
	
	return true
}

/**
 * Basic HTML sanitization to prevent XSS
 * @param input - String to sanitize
 * @returns Sanitized string
 */
export function sanitizeHtml(input: string): string {
	if (!input) return input
	
	const map: { [key: string]: string } = {
		"&": "&amp;",
		"<": "&lt;",
		">": "&gt;",
		'"': "&quot;",
		"'": "&#x27;",
		"/": "&#x2F;",
	}
	
	return input.replace(/[&<>"'/]/g, (char) => map[char])
}

/**
 * Sanitize user input for display name
 * @param input - Display name to sanitize
 * @returns Sanitized display name
 */
export function sanitizeDisplayName(input: string): string {
	if (!input) return input
	
	// Remove HTML tags and trim
	let sanitized = sanitizeHtml(input).trim()
	
	// Limit length
	const maxLength = 100
	if (sanitized.length > maxLength) {
		sanitized = sanitized.substring(0, maxLength)
	}
	
	return sanitized
}
